import { gql } from "@apollo/client";
import { ApiService } from "./ApiService";
import { OpResult } from "@sdflc/api-helpers";
import { Pagination, SortItem } from "./commonApiTypes";
import { AppPlan } from "./AppPlanService";

const ACCOUNT_LIST = gql`
  query AccountList($filter: AccountFilter, $params: PaginationAndSorting) {
    accountList(filter: $filter, params: $params) {
      code
      data {
        id
        spaceId
        name
        paymentSystem
        psCustomerId
        psSubscriptionId
        psSubscriptionStatus
        currentPlan
        appPlanPriceId
        trialEndsAt
        countryId
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_GET = gql`
  query AccountGet($id: ID!) {
    accountGet(id: $id) {
      code
      data {
        id
        spaceId
        name
        paymentSystem
        psCustomerId
        psSubscriptionId
        psSubscriptionStatus
        currentPlan
        appPlanPriceId
        trialEndsAt
        countryId
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_GET_MANY = gql`
  query AccountGetMany($ids: [ID]!) {
    accountGetMany(ids: $ids) {
      code
      data {
        id
        spaceId
        name
        paymentSystem
        psCustomerId
        psSubscriptionId
        psSubscriptionStatus
        currentPlan
        appPlanPriceId
        trialEndsAt
        countryId
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

const ACCOUNT_UPDATE = gql`
  mutation AccountUpdate($where: AccountWhereInput!, $params: AccountInput!) {
    accountUpdate(where: $where, params: $params) {
      code
      data {
        id
        spaceId
        name
        paymentSystem
        psCustomerId
        psSubscriptionId
        psSubscriptionStatus
        currentPlan
        trialEndsAt
        appPlanPriceId
        countryId
        status
      }
      errors {
        name
        errors
        warnings
      }
    }
  }
`;

export interface Account {
  id?: string | null | undefined;
  spaceId?: string | null | undefined;
  name?: string | null | undefined;
  paymentSystem?: string | null | undefined;
  psCustomerId?: string | null | undefined;
  psSubscriptionId?: string | null | undefined;
  psSubscriptionStatus?: string | null | undefined;
  currentPlan?: string | null | undefined;
  appPlanPriceId?: string | null | undefined;
  trialEndsAt?: string | null | undefined;
  countryId?: string | null | undefined;
  status?: string | null | undefined;
  appPlan?: AppPlan | null;
}

export interface AccountInput {
  name?: string | null | undefined;
  countryId?: string | null | undefined;
  status?: string | null | undefined;
}

export interface AccountFilter {
  id?: string[] | null | undefined;
  keyword?: string | null | undefined;
}

export interface AccountWhereInput {
  id?: string | null | undefined;
}

export interface AccountUpdateManyInput {
  where: AccountWhereInput;
  params: AccountInput;
}

/**
 * Account service that handles all account-related API calls
 * Supports full CRUD operations: list, get, getMany, create, createMany, update, updateMany, remove, removeMany
 */
export class AccountService {
  /**
   * Get a list of accounts with optional filtering, pagination, and sorting
   */
  static async list(
    filter?: AccountFilter,
    pagination?: Pagination,
    sortBy?: SortItem[]
  ): Promise<OpResult> {
    const result = await ApiService.executeQuery(ACCOUNT_LIST, {
      filter: filter || {},
      params: {
        pagination: pagination || undefined,
        sortBy: sortBy || undefined,
      },
    });
    return result;
  }

  /**
   * Get a single account by ID
   */
  static async get(id: string): Promise<OpResult> {
    const result = await ApiService.executeQuery(ACCOUNT_GET, {
      id,
    });
    return result;
  }

  /**
   * Get multiple accounts by their IDs
   */
  static async getMany(ids: string[]): Promise<OpResult> {
    const result = await ApiService.executeQuery(ACCOUNT_GET_MANY, {
      ids,
    });
    return result;
  }

  /**
   * Update an existing account
   */
  static async update(
    where: AccountWhereInput,
    params: AccountInput
  ): Promise<OpResult> {
    const result = await ApiService.executeMutation(ACCOUNT_UPDATE, {
      where,
      params,
    });
    return result;
  }
}
